import { RootState } from '@/store/store'
import { MatterArtifactAppliedTemplateRead } from '@/artifact-templates/schemas'
import { isAppliedTemplateFreshForSelection } from '@/artifact-templates/utils/applied-template-freshness'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { selectLoadedUserFilesById } from '@/user_files/files/store/selectors'

/**
 * The sticky template choice (template_user_file_id) for a Matter artifact, or undefined if none.
 * Use the id with `selectAgentV2TemplateName` to resolve a display name.
 */
export const selectMatterArtifactTemplateSelection = (state: RootState, props: { matterArtifactId: string | undefined | null }): string | undefined => {
  if (!props.matterArtifactId) return undefined
  return state.artifactTemplatesState.templateSelectionByMatterArtifactId[props.matterArtifactId]
}

/**
 * A template's display name from its UserFile id. Template state is stored by id only, so
 * names come from the loaded UserFiles. Returns undefined when the file isn't loaded or has
 * been deleted — callers then render the selected/applied state with no label.
 */
export const selectAgentV2TemplateName = (state: RootState, props: { templateUserFileId: string | undefined | null }): string | undefined => {
  if (!props.templateUserFileId) return undefined
  return selectLoadedUserFilesById(state)[props.templateUserFileId]?.original_filename
}

/**
 * The applied-template record for a Matter artifact version, but only while its rendered docx
 * still represents what the user would export now — i.e. the output isn't stale. It goes stale
 * two ways:
 *  - the version was edited after it was applied (`version.updated_at` moves past `last_applied_at`), or
 *  - a different template is now selected than the one that produced the output.
 * Either way the caller should re-apply rather than serve/label the existing output.
 *
 * Returns undefined when nothing has been applied or the output is stale. Returns the stored
 * object reference, so it needs no memoization.
 */
export const selectFreshMatterArtifactAppliedTemplate = (
  state: RootState,
  props: { matterArtifactVersionId: string | undefined | null }
): MatterArtifactAppliedTemplateRead | undefined => {
  if (!props.matterArtifactVersionId) return undefined
  const applied = state.artifactTemplatesState.appliedTemplateByMatterArtifactVersionId[props.matterArtifactVersionId]
  if (!applied) return undefined

  // Stale if the version was edited after the template was applied — the output no longer matches it.
  const version = selectMatterArtifactVersionById(state, props.matterArtifactVersionId)
  const updatedAt = version?.updated_at
  if (updatedAt != null && updatedAt > applied.last_applied_at) return undefined

  // Stale if the artifact's selected template has since changed: applying B doesn't make A's output
  // "applied". A null applied template means the template file was deleted post-export; keep that
  // output only while no replacement template is selected.
  const selectedTemplateId = selectMatterArtifactTemplateSelection(state, { matterArtifactId: version?.matter_artifact_id })
  if (
    !isAppliedTemplateFreshForSelection({
      appliedTemplateUserFileId: applied.template_user_file_id,
      selectedTemplateUserFileId: selectedTemplateId,
    })
  ) {
    return undefined
  }

  return applied
}
